import React from 'react';
import { UserUsageBreakdown } from '../../stores/subscriptionStore';
import { User, FileText, Clock, Activity } from 'lucide-react';

interface UserUsageTableProps {
  users: UserUsageBreakdown[];
  loading?: boolean;
  totalEncounters?: number;
  totalMinutes?: number;
}

export const UserUsageTable: React.FC<UserUsageTableProps> = ({
  users,
  loading = false,
  totalEncounters,
  totalMinutes
}) => {
  // An absent timestamp is a real state: a period can hold rows that carry none. Rendering it
  // through Date would print "Invalid Date".
  const formatDate = (value?: Date | string) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Usage Data</h3>
        <p className="text-gray-500">No usage has been recorded for this period yet.</p>
      </div>
    );
  }

  // Sort by encounters descending
  const sortedUsers = [...users].sort((a, b) => b.encounters - a.encounters);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Usage by User</h3>
        <p className="text-sm text-gray-500 mt-1">
          Breakdown of encounters and transcription minutes per user
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Encounters
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Minutes
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                % of Total
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Activity
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedUsers.map((user, index) => {
              const encounterPercent = totalEncounters
                ? Math.round((user.encounters / totalEncounters) * 100)
                : 0;

              return (
                <tr key={user.userId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-indigo-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        {user.identityResolved ? (
                          <>
                            <div className="text-sm font-medium text-gray-900">
                              {user.userName || user.email || 'Unnamed user'}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-500 italic">
                              Unknown provider
                            </div>
                            <div className="text-xs text-gray-400 font-mono">{user.userId}</div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end">
                      <FileText className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {user.encounters.toLocaleString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end">
                      <Clock className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {user.minutes.toLocaleString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-indigo-500 h-2 rounded-full"
                          style={{ width: `${Math.min(encounterPercent, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 w-12 text-right">
                        {encounterPercent}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                    {formatDate(user.lastActivityAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary footer */}
      {(totalEncounters !== undefined || totalMinutes !== undefined) && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-end space-x-8 text-sm">
            {totalEncounters !== undefined && (
              <div>
                <span className="text-gray-500">Total Encounters:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {totalEncounters.toLocaleString()}
                </span>
              </div>
            )}
            {totalMinutes !== undefined && (
              <div>
                <span className="text-gray-500">Total Minutes:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {totalMinutes.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserUsageTable;
